'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { createContext, Dispatch, PropsWithChildren, SetStateAction, Suspense, use, useContext, useEffect, useState } from 'react';
// 🧬 We use useRouter here from @bprogress/next for progress bar
import { useRouter } from '@bprogress/next';
import { SEARCH_PARAM_KEYS } from '@/constants/search';
import { useRouteChangeContext } from '@/context/RouteChangeContext';
import { SuggestionWorld, useWorldFuzzySearch } from '@/hooks/useWorldFuzzySearch';
import { usePathname } from '@/i18n/navigation';
import { Room } from '@/services/cms';
type HandleSearchOptions = {
  onAfterNavigate?: () => void;
};
type StartSearchFn = (value?: string, options?: HandleSearchOptions) => void;
type WorldSearchContextType = {
  isSearchSidebarOpen: boolean;
  setIsSearchSidebarOpen: Dispatch<SetStateAction<boolean>>;
  isMobileMenuInteracting: boolean;
  setIsMobileMenuInteracting: Dispatch<SetStateAction<boolean>>;
  mobileNavHeight: number;
  setMobileNavHeight: Dispatch<SetStateAction<number>>;
  worldsYouMayLike: Room[];
  startSearch: StartSearchFn;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  debouncedSearchValue: string;
  recentSearches: SuggestionWorld[];
  suggestionWorlds: SuggestionWorld[];
  clearRecentSearches: (id: string) => Promise<void>;
  isLoadingAutocomplete: boolean;
  isLoadingRecentSearch: boolean;
  appShowFilters: boolean;
  setAppShowFilters: Dispatch<SetStateAction<boolean>>;
  appSearchCleared: boolean;
  setAppSearchCleared: Dispatch<SetStateAction<boolean>>;
};
const WorldSearchContext = createContext<WorldSearchContextType | null>(null);
type WorldSearchProviderProps = PropsWithChildren<{
  worldsYouMayLikePromise: Promise<Room[]>;
}>;
function WorldsYouMayLikeSuspender(t0) {
  const $ = _c(4);
  const {
    promise,
    onLoad
  } = t0;
  const worldsYouMayLike = use(promise);
  let t1;
  let t2;
  if ($[0] !== onLoad || $[1] !== worldsYouMayLike) {
    t1 = () => {
      onLoad(worldsYouMayLike);
    };
    t2 = [onLoad, worldsYouMayLike];
    $[0] = onLoad;
    $[1] = worldsYouMayLike;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
export function WorldSearchProvider(t0) {
  const $ = _c(28);
  const {
    children,
    worldsYouMayLikePromise
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [worldsYouMayLike, setWorldsYouMayLike] = useState(t1);
  const [isSearchSidebarOpen, setIsSearchSidebarOpen] = useState(false);
  const [isMobileMenuInteracting, setIsMobileMenuInteracting] = useState(false);
  const [mobileNavHeight, setMobileNavHeight] = useState(72);
  const [appShowFilters, setAppShowFilters] = useState(false);
  const [appSearchCleared, setAppSearchCleared] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    setIsRouteLoading
  } = useRouteChangeContext();
  const pathname = usePathname();
  const {
    searchValue,
    setSearchValue,
    debouncedSearchValue,
    recentSearches,
    suggestionWorlds,
    isLoadingAutocomplete,
    isLoadingRecentSearch,
    clearRecentSearches
  } = useWorldFuzzySearch(isSearchSidebarOpen);
  let t2;
  if ($[1] !== pathname || $[2] !== router || $[3] !== searchParams || $[4] !== setIsRouteLoading) {
    t2 = (value, options) => {
      setIsRouteLoading(true);
      setTimeout(() => {
        if (!value) {
          router.push("/");
        } else {
          const params = new URLSearchParams({
            ...(pathname === "/search" ? Object.fromEntries(searchParams.entries()) : {}),
            [SEARCH_PARAM_KEYS.KEYWORD]: value
          });
          router.push(`/search?${params.toString()}`);
        }
        options?.onAfterNavigate?.();
      }, 250);
    };
    $[1] = pathname;
    $[2] = router;
    $[3] = searchParams;
    $[4] = setIsRouteLoading;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const startSearch = t2;
  let t3;
  if ($[6] !== appSearchCleared || $[7] !== appShowFilters || $[8] !== clearRecentSearches || $[9] !== debouncedSearchValue || $[10] !== isLoadingAutocomplete || $[11] !== isLoadingRecentSearch || $[12] !== isMobileMenuInteracting || $[13] !== isSearchSidebarOpen || $[14] !== mobileNavHeight || $[15] !== recentSearches || $[16] !== searchValue || $[17] !== setSearchValue || $[18] !== startSearch || $[19] !== suggestionWorlds || $[20] !== worldsYouMayLike) {
    t3 = {
      isSearchSidebarOpen,
      setIsSearchSidebarOpen,
      isMobileMenuInteracting,
      setIsMobileMenuInteracting,
      mobileNavHeight,
      setMobileNavHeight,
      worldsYouMayLike,
      startSearch,
      searchValue,
      setSearchValue,
      debouncedSearchValue,
      recentSearches,
      suggestionWorlds,
      clearRecentSearches,
      isLoadingAutocomplete,
      isLoadingRecentSearch,
      appShowFilters,
      setAppShowFilters,
      appSearchCleared,
      setAppSearchCleared
    };
    $[6] = appSearchCleared;
    $[7] = appShowFilters;
    $[8] = clearRecentSearches;
    $[9] = debouncedSearchValue;
    $[10] = isLoadingAutocomplete;
    $[11] = isLoadingRecentSearch;
    $[12] = isMobileMenuInteracting;
    $[13] = isSearchSidebarOpen;
    $[14] = mobileNavHeight;
    $[15] = recentSearches;
    $[16] = searchValue;
    $[17] = setSearchValue;
    $[18] = startSearch;
    $[19] = suggestionWorlds;
    $[20] = worldsYouMayLike;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== worldsYouMayLikePromise) {
    t4 = <Suspense fallback={null}><WorldsYouMayLikeSuspender promise={worldsYouMayLikePromise} onLoad={setWorldsYouMayLike} /></Suspense>;
    $[22] = worldsYouMayLikePromise;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== children || $[25] !== t3 || $[26] !== t4) {
    t5 = <WorldSearchContext.Provider value={t3}>{children}{t4}</WorldSearchContext.Provider>;
    $[24] = children;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  return t5;
}
export const useWorldSearchContext = () => {
  const value = useContext(WorldSearchContext);
  if (value == null) {
    throw new Error("useWorldSearchContext cannot be used outside of WorldSearchProvider");
  }
  return value;
};